import React from 'react';
import { Helmet } from 'react-helmet';
import { Briefcase, Code, Smartphone, TrendingUp } from "lucide-react";

const AIMLservices = () => {
  return (
    <section className="tech-services-section">
      {/* ✅ SEO Meta Tags */}
      <Helmet>
        <title>AI/ML Services | Pravisht Digital</title>
        <meta
          name="description"
          content="Explore Pravisht's AI/ML services including model development, MLOps, generative AI, and responsible AI governance for enterprise-scale innovation."
        />
        <meta
          name="keywords"
          content="Artificial Intelligence, Machine Learning, AI Services, ML Solutions, Generative AI, LLM, MLOps, AI Governance, Model Training, Data Annotation, Predictive Analytics, Pravisht"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Pravisht Digital" />
        <meta property="og:title" content="AI/ML Services | Pravisht Digital" />
        <meta
          property="og:description"
          content="AI/ML services that deliver intelligent automation, predictive analytics, and innovation at scale. Offered by Pravisht Digital."
        />
        <meta property="og:image" content="/testing_pravisht.jpg" />
        <meta property="og:url" content="https://www.pravisht.com/ai-ml-services" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="breadcrumb">
        <a href="/">Home</a> &gt; <span>AI/ML Services</span>
      </div>

      <div className="about-heading-section">
        <h1>Artificial Intelligence and Machine Learning Services</h1>
        <p>
          Quality is at the heart of digital success. At Pravisht, our Next Gen Testing Services offer a future-ready framework that ensures performance, security, and agility across platforms and domains. From automated pipelines to real-time validation, we deliver resilient and scalable QA solutions.
        </p>
      </div>

      <div className="about-image-fullscreen">
        <img src="/testing_pravisht.jpg" alt="Pravisht Digital" />
      </div>

      {/* --------------------- AI/ML Services Section ---------------------- */}
      <div className="core-offerings">
        <div className="how-we-help__content">
          <p className="how-we-help__description services-desc">Our Services</p>
          <h1 className="how-we-help__title">Artificial Intelligence and Machine Learning Services (AI/ML)</h1>
          <hr className="portfolio-horizontalline" />
        </div>

        <div className="about-heading-section">
          <p>
            At Pravisht, we deliver cutting-edge Artificial Intelligence and Machine Learning solutions tailored to drive intelligent automation, predictive analytics, and innovation at scale. Our AI/ML practice spans the full lifecycle—from data engineering and model development to testing, deployment, and responsible governance.
          </p>
        </div>

        <div className="offerings-grid">
          <div className="offering-card app-dev-card">
            <h4>1. AI/ML Development &amp; Engineering</h4>
            <p>We engineer scalable pipelines to bring your data to life:</p>
            <ul>
              <li>Data Preprocessing &amp; Feature Engineering</li>
              <li>Model Development &amp; Training Pipelines</li>
              <li>LLM (Large Language Model) Training &amp; Fine-Tuning</li>
              <li>MLOps: Model Deployment and Monitoring</li>
            </ul>
          </div>

          <div className="offering-card consulting-card">
            <h4>2. AI/ML Governance &amp; Strategy</h4>
            <p>We ensure AI is responsible, transparent, and compliant:</p>
            <ul>
              <li>AI Model Governance and Explainability</li>
              <li>Bias Detection and Fairness Audits</li>
              <li>Regulatory &amp; Compliance Alignment</li>
              <li>AI Strategy and Roadmap Design</li>
            </ul>
          </div>

          <div className="offering-card iot-blockchain-card">
            <h4>3. AI/ML Use Case Implementation</h4>
            <p>Customised solutions aligned to your business challenges:</p>
            <ul>
              <li>NLP and Computer Vision Applications</li>
              <li>Predictive &amp; Recommendation Engines</li>
              <li>Generative AI (LLMs), RAG-based Apps</li>
              <li>Autonomous Agentic and Automation Models</li>
            </ul>
          </div>

          <div className="offering-card automation-card">
            <h4>4. AI/ML Testing &amp; Validation</h4>
            <p>AI should be trusted, not just smart:</p>
            <ul>
              <li>Accuracy, Precision, and Recall Evaluation</li>
              <li>Robustness, Drift &amp; Edge Case Testing</li>
              <li>Bias &amp; Ethical Risk Assessments</li>
              <li>Model Validation and Simulation Testing</li>
            </ul>
          </div>

          <div className="offering-card mobile-apps-card">
            <h4>5. Other AI/ML Services</h4>
            <p>Extended offerings for advanced AI deployment:</p>
            <ul>
              <li>Data Annotation and Synthetic Data Generation</li>
              <li>Model Migration and Re-Training</li>
              <li>MCP (Modal Context Protocol) Based Solutions</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="engagement-layers-and-why-choose" style={{ backgroundColor: '#f1f4f3', marginBottom: '60px' }}>
        <div className="how-we-help__content">
          <h3 className="section-subtitle">Our Value Framework</h3>
          <p>We support your AI journey with a proven lifecycle approach:</p>
        </div>

        <div className="bottom-sections-grid">
          <div className="how-we-help__cards">
            {[
              { icon: <Code size={40} />, title: "Model Development", desc: "Build and train intelligent AI/ML models" },
              { icon: <Briefcase size={40} />, title: "Generative AI & RAG", desc: "Deploy LLMs and retrieval-augmented generation apps" },
              { icon: <Smartphone size={40} />, title: "AI Use Case Implementation", desc: "Deliver production-ready AI use cases" },
              { icon: <TrendingUp size={40} />, title: "MLOps & Deployment", desc: "Operationalize models with monitoring and automation" }
            ].map((card, i) => (
              <div className="how-we-help__card" key={i} style={{ height: '190px', cursor: 'default' }}>
                <h3 className="card-title">{card.title}</h3>
                <p className="card-desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIMLservices;
